/**
 * PublicJobShare — Iter-290b
 *
 * Marketing landing page rendered at /jobs/:shiftId for shared job links.
 * Anonymous-friendly. Pulls public-safe summary from /api/public/jobs/{id}.
 *
 * UX:
 *   • Hero card with company logo + job title + location + dates + pay
 *   • Short description from duties/additional_notes
 *   • "Apply now" CTA → routes to /login?redirect=/jobs/:id/apply
 *   • Secondary ShareJobButton — anyone landing here can re-share
 *   • Powered-by-iEvntür footer (matched by PUBLIC_PATH_MATCHERS via /jobs/*)
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Calendar as CalIcon, DollarSign, Briefcase, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import ShareJobButton from '../components/ShareJobButton';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDateLong(iso) {
  if (!iso) return null;
  try {
    const [y, m, d] = iso.split('-');
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    return dt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

export default function PublicJobShare() {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/jobs/${shiftId}`);
        if (!cancel) {
          setJob(res.data);
          // Iter-290c (#1) — best-effort view log; ignore failures
          try { axios.post(`${API}/api/public/jobs/${shiftId}/view`).catch(() => {}); } catch (_e) {}
        }
      } catch (e) {
        if (!cancel) setError(e.response?.data?.detail || 'Job not found');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [shiftId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-cyan-50 to-slate-100">
        <div className="text-slate-500 text-sm">Loading job details…</div>
      </div>
    );
  }
  if (error || !job) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-50 via-cyan-50 to-slate-100">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-md border border-slate-200 p-8 text-center" data-testid="public-job-error">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Job not available</h1>
          <p className="text-sm text-slate-600 mb-6">{error || 'This job listing is no longer available.'}</p>
          <Button onClick={() => navigate('/login')} data-testid="public-job-error-login-btn">Browse iEvntür</Button>
        </div>
      </div>
    );
  }

  const applyHref = `/login?redirect=${encodeURIComponent(job.apply_path || `/jobs/${job.id}/apply`)}`;
  const dateLabel = job.date_range_display || formatDateLong(job.date);

  return (
    <div className="min-h-screen bg-[#0b1023] pb-12 relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(120%_70%_at_50%_0%,rgba(34,211,238,0.22)_0%,rgba(34,211,238,0.05)_45%,transparent_70%)]" aria-hidden="true" />
      <div className="relative max-w-2xl mx-auto px-4 pt-10 sm:pt-16">
        <div className="bg-white/95 backdrop-blur rounded-3xl shadow-2xl border border-white/10 overflow-hidden ring-1 ring-cyan-500/10" data-testid="public-job-card">
          <div className="relative bg-gradient-to-br from-cyan-500 via-cyan-600 to-teal-700 px-6 sm:px-10 py-10 text-white">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(60%_60%_at_50%_0%,#fff_0%,transparent_70%)]" aria-hidden="true" />
            <div className="relative flex items-start gap-4">
              {job.company_logo_url ? (
                <img src={job.company_logo_url} alt={job.company} className="w-16 h-16 rounded-2xl bg-white p-2 object-contain shrink-0 shadow-lg ring-1 ring-white/30" data-testid="public-job-company-logo" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shrink-0 ring-1 ring-white/30">
                  <Briefcase className="w-8 h-8 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-90" data-testid="public-job-company">{job.company || 'Open opportunity'}</div>
                <h1 className="text-3xl sm:text-4xl font-bold mt-2 leading-[1.1] tracking-tight" data-testid="public-job-title">{job.title}</h1>
                {job.role_name && <div className="text-sm opacity-90 mt-2" data-testid="public-job-role">{job.role_name}</div>}
                {job.is_multi_day && (
                  <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[11px] font-semibold uppercase tracking-wider" data-testid="public-job-multi-day-badge">
                    Multi-day · {job.additional_dates?.length || 0} shifts
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="px-6 sm:px-10 py-7 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {job.location_display && (
                <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-0.5"><MapPin className="w-3 h-3 text-cyan-700" /> Location</div>
                  <div className="text-sm text-slate-800 font-medium leading-snug" data-testid="public-job-location">{job.location_display}</div>
                </div>
              )}
              {(dateLabel || job.start_time) && (
                <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-0.5"><CalIcon className="w-3 h-3 text-cyan-700" /> {job.is_multi_day ? 'Dates' : 'Date'}</div>
                  <div className="text-sm text-slate-800 font-medium leading-snug" data-testid="public-job-date">{dateLabel || '—'}</div>
                  {(job.start_time || job.end_time) && (
                    <div className="text-xs text-slate-500 mt-0.5" data-testid="public-job-time">{[job.start_time, job.end_time].filter(Boolean).join(' – ')}</div>
                  )}
                </div>
              )}
              {job.pay_display && (
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/60 rounded-xl px-3 py-2.5 border border-emerald-100">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-0.5"><DollarSign className="w-3 h-3" /> Pay</div>
                  <div className="text-base text-emerald-800 font-bold leading-snug" data-testid="public-job-pay">{job.pay_display}</div>
                </div>
              )}
            </div>

            {job.is_multi_day && Array.isArray(job.additional_dates) && job.additional_dates.length > 0 && (
              <div className="bg-cyan-50/60 border border-cyan-100 rounded-xl p-4" data-testid="public-job-schedule">
                <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700 mb-2">Schedule</div>
                <ul className="space-y-1 text-sm text-slate-700">
                  {job.additional_dates.map((d, i) => (
                    <li key={i} className="flex items-center justify-between border-b border-cyan-100/60 last:border-0 py-1">
                      <span className="font-medium">{formatDateLong(d.date)}</span>
                      <span className="text-xs text-slate-500">{[d.start_time, d.end_time].filter(Boolean).join(' – ') || '—'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {job.description && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">About this opportunity</div>
                <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed" data-testid="public-job-description">{job.description}</p>
              </div>
            )}

            {job.dress_code && (
              <div className="bg-slate-50 rounded-xl border border-slate-200 px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">Dress code</div>
                <div className="text-sm text-slate-700">{job.dress_code}</div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button size="lg" className="flex-1 bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white text-base font-bold shadow-lg shadow-cyan-600/20 transition-transform hover:-translate-y-0.5" onClick={() => navigate(applyHref)} data-testid="public-job-apply-btn">
                Apply on iEvntür →
              </Button>
              <ShareJobButton variant="button" testIdSuffix={`public-${job.id}`} job={{ id: job.id, title: job.title, company: job.company, location: job.location_display, dateRange: dateLabel, payRate: job.pay_display, description: job.description, roleName: job.role_name }} />
            </div>
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-cyan-100/80">
          Hosted on iEvntür — built for talent, brands, and event staffing.
        </div>
      </div>
    </div>
  );
}
